import { InfoTip, type InfoTipContent } from "@/components/InfoTip";
import { formatDate, formatNumber } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { LSLI } from "@/types/api";

interface Props {
  lsli: LSLI | null;
}

export function LSLICard({ lsli }: Props): JSX.Element {
  if (!lsli) {
    return (
      <section className="card">
        <h2 className="card-title">Lead service line inventory</h2>
        <p className="text-sm text-ink-300">No Lead Service Line Inventory on file with FDEP. An LSLI is a federal requirement under the Lead and Copper Rule Revisions — if this system hasn't submitted one, that's a compliance gap to raise with your DEP contact. AquaTrack shows the state's copy once it's filed.</p>
      </section>
    );
  }

  const allClear =
    lsli.lead_count === 0 &&
    lsli.galvanized_requiring_replacement_count === 0 &&
    lsli.unknown_count === 0;

  return (
    <section className="card">
      <header className="flex items-center justify-between">
        <h2 className="card-title m-0 inline-flex items-center">
          Lead service line inventory
          <InfoTip {...TIPS.lsli} />
        </h2>
        {allClear && <span className="pill-ok">All clear</span>}
      </header>
      <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="Lead" tip={TIPS.lslLead} value={lsli.lead_count} highlight={lsli.lead_count > 0} />
        <Stat
          label="GRR"
          tip={TIPS.lslGrr}
          value={lsli.galvanized_requiring_replacement_count}
          highlight={lsli.galvanized_requiring_replacement_count > 0}
        />
        <Stat
          label="Unknown"
          tip={TIPS.lslUnknown}
          value={lsli.unknown_count}
          highlight={lsli.unknown_count > 0}
        />
        <Stat label="Non-lead" tip={TIPS.lslNonLead} value={lsli.non_lead_count} />
      </dl>
      <div className="mt-3 text-sm border-t border-ink-700 pt-3">
        <dt className="text-ink-300 inline-flex items-center">Submitted<InfoTip {...TIPS.lslSubmitted} /></dt>
        <dd>{formatDate(lsli.submission_date)}</dd>
      </div>
    </section>
  );
}

function Stat({
  label,
  tip,
  value,
  highlight,
}: {
  label: string;
  tip?: InfoTipContent;
  value: number;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-ink-300 text-xs uppercase tracking-wide inline-flex items-center">
        {label}
        {tip && <InfoTip {...tip} />}
      </dt>
      <dd
        className={`text-2xl font-semibold tabular-nums ${
          highlight ? "text-severity-high" : ""
        }`}
      >
        {formatNumber(value)}
      </dd>
    </div>
  );
}
